import { useState, InputHTMLAttributes, ReactNode } from 'react'

import { FieldErrors, Control, Controller, FieldValues, Path } from 'react-hook-form'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import type { StylesConfig } from 'react-select'
import { BottomSheetSelect } from '.'
import { BottomSheetOption } from './BottomSheetSelect'
import useViewPort from '@/hooks/useViewPort'
import React from 'react'
import FieldErrorComponent, { RequiredMark } from './FieldError'

interface PasswordInputProps<TFieldValues extends FieldValues = FieldValues> {
	name: string
	placeholder?: string
	refCallback?: any
	errors: FieldErrors
	control?: Control<TFieldValues>
	register?: any
	className?: string
	inputVariant?: 'default' | 'auth-ember'
}

/* Password Input */
const PasswordInput = <TFieldValues extends FieldValues = FieldValues>({
	name,
	placeholder,
	refCallback,
	errors,
	register,
	className,
	inputVariant = 'default',
}: PasswordInputProps<TFieldValues>) => {
	const [showPassword, setShowPassword] = useState<boolean>(false)
	const wrapClass =
		inputVariant === 'auth-ember'
			? 'auth-ember-password-wrap'
			: 'flex w-full items-center overflow-hidden rounded-[0.6rem] border border-[hsl(var(--border))] bg-[hsl(var(--input))] shadow-sm transition-colors focus-within:border-[hsl(var(--ring))] focus-within:ring-1 focus-within:ring-[hsl(var(--ring))]/35'
	const toggleClass =
		inputVariant === 'auth-ember'
			? 'auth-ember-password-toggle'
			: 'flex min-h-[2.5rem] min-w-[44px] shrink-0 items-center justify-center border-s border-[hsl(var(--border))] px-3 text-[hsl(var(--muted-foreground))] transition-colors hover:text-[hsl(var(--foreground))]'
	return (
		<>
			<div className={wrapClass}>
				<input
					type={showPassword ? 'text' : 'password'}
					placeholder={placeholder}
					name={name}
					id={name}
					ref={(r: HTMLInputElement) => {
						if (refCallback) refCallback(r)
					}}
					className={`${className} min-h-[2.5rem] flex-1 !rounded-none !border-0 !bg-transparent !shadow-none !ring-0 ${errors && errors[name] ? 'text-red-700' : ''}`}
					{...(register ? register(name) : {})}
					autoComplete={name}
				/>
				<button
					type="button"
					aria-label={showPassword ? 'Hide password' : 'Show password'}
					className={toggleClass}
					onClick={() => {
						setShowPassword(!showPassword)
					}}
				>
					{showPassword ? (
						<svg
							viewBox="0 0 24 24"
							aria-hidden="true"
							className="h-5 w-5"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.8"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="M3 3l18 18" />
							<path d="M10.58 10.58a2 2 0 102.83 2.83" />
							<path d="M9.88 5.09A9.77 9.77 0 0112 4.86c5 0 8.27 3.11 9.5 7.14a11.8 11.8 0 01-2.13 3.59" />
							<path d="M6.61 6.62C4.62 7.9 3.35 9.81 2.5 12c1.23 4.03 4.5 7.14 9.5 7.14a9.8 9.8 0 004.18-.92" />
						</svg>
					) : (
						<svg
							viewBox="0 0 24 24"
							aria-hidden="true"
							className="h-5 w-5"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.8"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="M2.5 12C3.73 7.97 7 4.86 12 4.86S20.27 7.97 21.5 12C20.27 16.03 17 19.14 12 19.14S3.73 16.03 2.5 12z" />
							<circle cx="12" cy="12" r="3" />
						</svg>
					)}
				</button>
			</div>
		</>
	)
}

interface FormInputProps<TFieldValues extends FieldValues = FieldValues> extends InputHTMLAttributes<HTMLInputElement> {
	label?: string
	type?: string
	name: string
	placeholder?: string
	register?: any
	errors?: any
	control?: Control<TFieldValues>
	className?: string
	labelContainerClassName?: string
	labelClassName?: string
	containerClass?: string
	refCallback?: any
	children?: ReactNode
	rows?: number
	options?: BottomSheetOption[]
	isMultiSelect?: boolean
	inputVariant?: 'default' | 'auth-ember'
}

const FieldError = ({ errors, name }: { errors: any; name: string }) => (
	<FieldErrorComponent errors={errors} name={name} />
)

const FormInput = <TFieldValues extends FieldValues = FieldValues>({ label, type, name, placeholder, register, errors, className, labelClassName, labelContainerClassName, containerClass, refCallback, children, rows, options, isMultiSelect, inputVariant, ...otherProps }: FormInputProps<TFieldValues>) => {
	const { width } = useViewPort()
	const isMobile = width < 768
	const Tag = type === 'textarea' ? 'textarea' : type === 'select' ? 'select' : 'input'
	const themedSelectStyles: StylesConfig<BottomSheetOption, boolean> = {
		control: (base, state) => ({
			...base,
			minHeight: '3rem',
			borderRadius: '0.95rem',
			borderColor: state.isFocused || state.selectProps.menuIsOpen ? 'hsl(var(--ring))' : 'hsl(var(--border))',
			backgroundColor: 'hsl(var(--input))',
			boxShadow: state.isFocused ? '0 0 0 1px hsl(var(--ring) / 0.35)' : 'none',
			transition: 'colors 150ms ease',
			'&:hover': {
				borderColor: state.isFocused || state.selectProps.menuIsOpen ? 'hsl(var(--ring))' : 'hsl(var(--border))',
			},
		}),
		valueContainer: (base) => ({
			...base,
			padding: '0 0.75rem',
		}),
		input: (base) => ({
			...base,
			color: '#ffffff',
			caretColor: '#ffffff',
			margin: 0,
			padding: 0,
		}),
		placeholder: (base) => ({
			...base,
			color: '#c89188',
		}),
		singleValue: (base) => ({
			...base,
			color: '#ffffff',
		}),
		multiValue: (base) => ({
			...base,
			backgroundColor: 'rgb(147 197 253 / 0.18)',
			border: '1px solid rgb(147 197 253 / 0.28)',
			borderRadius: '0.7rem',
		}),
		multiValueLabel: (base) => ({
			...base,
			color: '#ffffff',
			fontSize: '0.875rem',
			paddingLeft: '0.55rem',
			paddingRight: '0.35rem',
		}),
		multiValueRemove: (base) => ({
			...base,
			color: '#c89188',
			borderRadius: '0 0.7rem 0.7rem 0',
			'&:hover': {
				backgroundColor: 'rgb(225 29 42 / 0.22)',
				color: '#ffffff',
			},
		}),
		indicatorSeparator: (base) => ({
			...base,
			backgroundColor: 'rgb(200 145 136 / 0.35)',
		}),
		dropdownIndicator: (base) => ({
			...base,
			color: '#c89188',
			'&:hover': {
				color: '#ffffff',
			},
		}),
		clearIndicator: (base) => ({
			...base,
			color: '#c89188',
			'&:hover': {
				color: '#ffffff',
			},
		}),
		menu: (base) => ({
			...base,
			borderRadius: 0,
			overflow: 'hidden',
			backgroundColor: 'hsl(350 18% 22%)',
			border: '1px solid hsl(354 32% 32% / 0.85)',
			boxShadow: '0 18px 40px rgba(0, 0, 0, 0.35)',
			zIndex: 20,
		}),
		menuList: (base) => ({
			...base,
			padding: 0,
		}),
		option: (base, state) => ({
			...base,
			backgroundColor: state.isFocused ? 'rgb(147 197 253 / 0.55)' : state.isSelected ? 'rgb(147 197 253 / 0.7)' : 'hsl(350 18% 22%)',
			color: '#ffffff',
			cursor: 'pointer',
		}),
	}

	const getOptionsFromChildren = (children: ReactNode): BottomSheetOption[] => {
		const extractedOptions: BottomSheetOption[] = []
		React.Children.forEach(children, (child) => {
			if (React.isValidElement(child)) {
				const element = child as React.ReactElement<any>
				if (element.type === 'option') {
					extractedOptions.push({
						label: element.props.children?.toString() || '',
						value: element.props.value,
					})
				} else if (element.props.children) {
					extractedOptions.push(...getOptionsFromChildren(element.props.children))
				}
			}
		})
		return extractedOptions
	}

	const rawOptions = options && options.length > 0 ? options : getOptionsFromChildren(children)

	const placeholderOption = rawOptions.find((opt) => opt.value === '' || opt.value === undefined)
	const selectPlaceholder = placeholder || placeholderOption?.label || 'Select option'
	const finalOptions = rawOptions.filter((opt) => opt.value !== '' && opt.value !== undefined)

	return (
		<>
			{type === 'hidden' ? (
				<input type={type} name={name} {...(register ? register(name) : {})} {...otherProps} />
			) : (
				<>
				{type === 'password' ? (
					<>
						<div className={containerClass ?? ''}>
							{label && (
								<div className={labelContainerClassName ?? ''}>
									<label className={labelClassName ?? ''} htmlFor={name}>
										{label}
										{otherProps.required && <RequiredMark />}
									</label>
									{children}
								</div>
							)}
							<PasswordInput name={name} placeholder={placeholder} refCallback={refCallback} errors={errors} register={register} className={className} inputVariant={inputVariant} />
							<FieldError errors={errors} name={name} />
						</div>
					</>
					) : (
						<>
						{type === 'textarea' ? (
							<>
								<div className={`${containerClass ?? ''} relative`}>
									{label ? (
										<label className={labelClassName ?? ''} htmlFor={name}>
											{label}
											{otherProps.required && <RequiredMark />}
										</label>
									) : null}
									<Tag
										placeholder={placeholder}
										name={name}
										id={name}
										rows={rows}
										ref={(r: HTMLInputElement) => {
											if (refCallback) refCallback(r)
										}}
										className={`${className} ${errors && errors[name] ? 'border-red-500 focus:border-red-500 text-red-700  pe-10' : ''}`}
										{...(register ? register(name) : {})}
										{...otherProps}
										autoComplete={name}
									/>
									<FieldError errors={errors} name={name} />
								</div>
							</>
							) : (
								<>
									{type === 'select' ? (
										<>
											<div className={`${containerClass ?? ''} relative`}>
												{label && (
													<label className={labelClassName ?? ''} htmlFor={name}>
														{label}
														{otherProps.required && <RequiredMark />}
													</label>
												)}
												<Tag
													name={name}
													id={name}
													ref={(r: HTMLSelectElement) => {
														if (refCallback) refCallback(r)
													}}
													className={className}
													{...(register ? register(name) : {})}
													{...otherProps}
													autoComplete={name}
												>
													{children}
												</Tag>
												<FieldError errors={errors} name={name} />
											</div>
										</>
									) : type === 'creatable-select' ? (
										<div className={`${containerClass ?? ''} relative`}>
											{label && (
												<label className={labelClassName ?? ''} htmlFor={name}>
													{label}
													{otherProps.required && <RequiredMark />}
												</label>
											)}
											{otherProps.control ? (
												<Controller
													name={name as Path<TFieldValues>}
													control={otherProps.control}
													rules={{ required: otherProps.required ? 'This field is required' : false }}
													render={({ field, fieldState }) => (
														<>
															<CreatableSelect
																inputId={name}
																classNamePrefix="react-select"
																className={`react-select-container ${fieldState.error ? 'is-invalid' : ''}`}
																styles={themedSelectStyles}
																options={finalOptions}
																placeholder={selectPlaceholder}
																isClearable
																value={finalOptions.find((opt) => opt.value === field.value) ?? (field.value ? { label: String(field.value), value: field.value } : null)}
																onChange={(selectedOption: any) => {
																	field.onChange(selectedOption?.value ?? '')
																}}
																onCreateOption={(inputValue: string) => {
																	field.onChange(inputValue)
																}}
															/>
															{fieldState.error && <p className="text-xs text-red-600 mt-2">{fieldState.error.message}</p>}
														</>
													)}
												/>
											) : null}
										</div>
									) : type === 'bottom-sheet' ? (
										<div className={`${containerClass ?? ''} relative`}>
											{isMobile ? (
												otherProps.control ? (
													<Controller
														name={name as Path<TFieldValues>}
														control={otherProps.control}
														rules={{ required: otherProps.required ? 'This field is required' : false }}
														render={({ field, fieldState }) => (
															<>
																<BottomSheetSelect label={label} placeholder={selectPlaceholder} options={finalOptions} value={field.value} onSelect={field.onChange} labelClassName={labelClassName} triggerClassName={`${className} ${fieldState.error ? 'border-red-500' : ''}`} isMultiSelect={isMultiSelect} />
																{fieldState.error && <p className="text-xs text-red-600 mt-2">{fieldState.error.message}</p>}
															</>
														)}
													/>
												) : (
													<BottomSheetSelect
														label={label}
														placeholder={selectPlaceholder}
														options={finalOptions}
														value={otherProps.value}
														onSelect={(val) => {
															if (otherProps.onChange) {
																otherProps.onChange({
																	target: {
																		name: name,
																		id: name,
																		value: val,
																	},
																} as any)
															}
														}}
														labelClassName={labelClassName}
														triggerClassName={`${className} ${errors && errors[name] ? 'border-red-500' : ''}`}
														isMultiSelect={isMultiSelect}
													/>
												)
											) : (
												<>
													{label && (
														<label className={labelClassName ?? ''} htmlFor={name}>
															{label}
															{otherProps.required && <RequiredMark />}
														</label>
													)}
													{isMultiSelect ? (
														otherProps.control ? (
															<Controller
																name={name as Path<TFieldValues>}
																control={otherProps.control}
																rules={{ required: otherProps.required ? 'This field is required' : false }}
																render={({ field, fieldState }) => (
																	<>
																		<Select
																			classNamePrefix="react-select"
																			className={`react-select-container ${fieldState.error ? 'is-invalid' : ''}`}
																			styles={themedSelectStyles}
																			options={finalOptions}
																			isMulti={true}
																			placeholder={selectPlaceholder}
																			value={finalOptions?.filter((opt) => Array.isArray(field.value) && field.value.includes(opt.value))}
																			onChange={(selectedOptions: any) => {
																				field.onChange(selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [])
																			}}
																		/>
																		{fieldState.error && <p className="text-xs text-red-600 mt-2">{fieldState.error.message}</p>}
																	</>
																)}
															/>
														) : (
															<Select
																classNamePrefix="react-select"
																className={`react-select-container ${errors && errors[name] ? 'is-invalid' : ''}`}
																styles={themedSelectStyles}
																options={finalOptions}
																isMulti={true}
																placeholder={selectPlaceholder}
																value={finalOptions?.filter((opt) => Array.isArray(otherProps.value) && otherProps.value.includes(opt.value))}
																onChange={(selectedOptions: any) => {
																	if (otherProps.onChange) {
																		const values = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : []
																		otherProps.onChange({
																			target: {
																				name: name,
																				id: name,
																				value: values,
																			},
																		} as any)
																	}
																}}
															/>
														)
													) : (
														<>
															<select
																name={name}
																id={name}
																ref={(r: HTMLSelectElement) => {
																	if (refCallback) refCallback(r)
																}}
																className={`${className} ${errors && errors[name] ? 'border-red-500 focus:border-red-500 text-red-700' : ''}`}
																{...(register ? register(name) : {})}
																{...otherProps}
																autoComplete={name}
															>
																<option value="">{selectPlaceholder}</option>
																{finalOptions?.map((opt, idx) => (
																	<option key={idx} value={opt.value}>
																		{opt.label}
																	</option>
																))}
															</select>
															{errors && errors[name] && <p className="text-xs text-red-600 mt-2">{errors[name]['message']}</p>}
														</>
													)}
												</>
											)}
										</div>
									) : (
										<>
											{type === 'checkbox' || type === 'radio' ? (
												<>
													<div className={containerClass ?? ''}>
														<label className={labelClassName ?? ''}>{label}</label>
														<div className="flex items-center">
															<input
																data-switch="success"
																type={type}
																name={name}
																id={name}
																ref={(r: HTMLInputElement) => {
																	if (refCallback) refCallback(r)
																}}
																className={`${className} ${errors && errors[name] ? 'border-red-500 focus:border-red-500 text-red-700  pe-10' : ''}`}
																{...(register ? register(name) : {})}
																{...otherProps}
															/>
															<label data-on-label="Yes" data-off-label="No" htmlFor={name}></label>
														</div>
													</div>
												</>
											) : (
									<>
												<div className={containerClass ?? ''}>
													{label && (
														<label className={labelClassName ?? ''} htmlFor={name}>
															{label}
															{otherProps.required && <RequiredMark />}
														</label>
													)}
													<div className="relative">
														<input
															type={type}
															placeholder={placeholder}
															name={name}
															id={name}
															ref={(r: HTMLInputElement) => {
																if (refCallback) refCallback(r)
															}}
															className={`${className} ${errors && errors[name] ? 'border-red-500 focus:border-red-500 text-red-700  pe-10' : ''}`}
															{...(register ? register(name) : {})}
															{...otherProps}
															autoComplete={name}
														/>
														{errors && errors[name] && (
															<div className="absolute inset-y-0 end-0 flex items-center pointer-events-none pe-3">
																<i className="ri-error-warning-fill text-xl text-red-500" />
															</div>
														)}
													</div>
													<FieldError errors={errors} name={name} />
													{children ? children : null}
												</div>
											</>
											)}
										</>
									)}
								</>
							)}
						</>
					)}
				</>
			)}
		</>
	)
}

export default FormInput
